<template>
  <div>
    <QuestionsSubmenu>
      <button
        v-if="hasEditionRights"
        type="button"
        class="inline-flex items-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white ring-1 ring-green-200 shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        @click="openModal"
      >
        <i class="fas fa-plus-circle -ml-1 mr-2 h-5 w-5" />
        Créer une question
      </button>
    </QuestionsSubmenu>

    <PageContent>
      <MVPTable
        :gridData="gridData"
        :options="gridOptions"
        statusBarType="displayed"
        @rowDoubleClicked="openModal"
        @gridReady="onGridReady"
        @selectionChanged="selectedQuestions = $event"
      />
      <button
        v-if="hasEditionRights && selectedQuestions.length > 0"
        class="button is-danger mt-3"
        @click="deleteSelectedQuestions"
      >
        <i class="fas fa-trash"></i>
      </button>
    </PageContent>

    <t-modal
      name="question-form"
      variant="articleForm"
      hide-close-button
    >
      <MVPQuestionsForm
        v-if="currentQuestion"
        :question="currentQuestion"
        :questionKey="currentQuestion.uuid"
        :orderedUuids="orderedUuids"
        :currentIndex="currentIndex"
        @refresh="loadQuestionsWithPosition"
        @navigate="handleNavigate"
      />
    </t-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { http } from '@/services/api.service'
import PageContent from '@/components/PageContent.vue'
import MVPTable from '@/components/agGrid/MVPTable.vue'
import QuestionsSubmenu from './QuestionsSubmenu.vue'
import ListRowDetail from './ListRowDetail.vue'
import MVPQuestionsForm from './Form.vue'
import RenotificationCellRenderer from './RenotificationCellRenderer.vue'
import RenotificationTooltip from './RenotificationTooltip.vue'
import gridScrollPosition from '@/mixins/gridScrollPosition'
import { renotificationState } from '@/utils/faqRenotification'

const getDocumentsCount = (params) => {
  return params.data.documents.length
}

const RESPONSE_EXPORT_COL_ID = 'responseExport'

export default {
  name: 'app-questions-list',
  mixins: [gridScrollPosition],
  components: {
    PageContent,
    QuestionsSubmenu,
    MVPTable,
    ListRowDetail, //eslint-disable-line
    RenotificationCellRenderer, //eslint-disable-line
    RenotificationTooltip, //eslint-disable-line
    MVPQuestionsForm
  },
  computed: {
    ...mapGetters([
      'roles',
      'domain'
    ]),
    hasEditionRights () {
      return this.roles.some(role => ['admin', 'director', 'community-manager'].includes(role))
    },
    canRenotify () {
      return this.roles.some(role => ['admin', 'community-manager'].includes(role))
    },
    gridOptions () {
      return {
        masterDetail: true,
        tooltipShowDelay: 200,
        detailCellRenderer: 'ListRowDetail',
        detailRowAutoHeight: true,
        rowSelection: 'multiple',
        suppressRowClickSelection: true,
        getContextMenuItems: this.getContextMenuItems,
        columnDefs: [
          {
            width: 45,
            checkboxSelection: true,
            headerCheckboxSelection: true,
            suppressMenu: true,
            sortable: false,
            filter: false,
            resizable: false,
            hide: !this.hasEditionRights
          },
          {
            headerName: 'Type',
            cellRenderer: 'agGroupCellRenderer',
            field: 'type.name',
            sortable: true,
            filter: 'agSetColumnFilter',
            filterParams: {
              applyMiniFilterWhileTyping: true
            }
          },
          {
            headerName: 'Catégorie',
            field: 'category.label',
            filter: 'agSetColumnFilter',
            sortable: true
          },
          {
            headerName: 'Question',
            field: 'title',
            filter: 'agTextColumnFilter',
            width: 300,
            sortable: true
          },
          {
            headerName: 'Réponse',
            colId: RESPONSE_EXPORT_COL_ID,
            valueGetter: (params) => this.stripHtml(params.data && params.data.response),
            hide: true,
            suppressColumnsToolPanel: true
          },
          {
            headerName: 'Lectures',
            type: 'numericColumn',
            field: 'consultations_count',
            filter: 'agNumberColumnFilter',
            width: 120,
            sortable: true
          },
          {
            headerName: 'PJ',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
            valueGetter: getDocumentsCount,
            width: 80,
            sortable: true
          },
          {
            headerName: 'Avis',
            headerClass: 'ag-center-aligned-header',
            field: 'rating.percent',
            filter: 'agNumberColumnFilter',
            cellRenderer: 'RatingCellRenderer',
            minWidth: 120,
            sortable: true
          },
          {
            headerName: 'Activée',
            headerClass: 'ag-center-aligned-header',
            field: 'is_enabled',
            sortable: true,
            cellRenderer: 'BooleanCellRenderer',
            cellRendererParams: {
              onClick: this.toggleQuestionEnabled
            },
            width: 100,
            cellStyle: {
              'textAlign': 'center'
            },
            filter: 'agSetColumnFilter',
            filterParams: {
              cellRenderer: 'BooleanFilterCellRenderer',
              buttons: ['reset']
            }
          },
          {
            headerName: 'Identifiant unique',
            field: 'uuid',
            width: 100
          },
          {
            headerName: 'Renotification',
            colId: 'renotification',
            headerClass: 'ag-center-aligned-header',
            cellRenderer: 'RenotificationCellRenderer',
            cellRendererParams: {
              onClick: this.renotifyQuestion
            },
            tooltipComponent: 'RenotificationTooltip',
            // The tooltip reads the row itself; ag-Grid only opens it when this value is not empty.
            tooltipValueGetter: () => 'renotification',
            width: 130,
            suppressMenu: true,
            sortable: false,
            filter: false,
            hide: !this.canRenotify,
            suppressColumnsToolPanel: !this.canRenotify
          },
          {
            cellRenderer: 'IconCellRenderer',
            cellRendererParams: {
              iconClass: 'fas fa-trash text-red-600',
              onClick: this.deleteQuestion
            },
            width: 60,
            suppressMenu: true,
            hide: !this.hasEditionRights
          }
        ]
      }
    }
  },
  data () {
    return {
      loading: false,
      questions: [],
      all: [],
      searchInput: '',
      currentQuestion: null,
      orderedUuids: [],
      currentIndex: 0,
      selectedQuestions: [],
      gridApi: null,
      columnApi: null,
      gridData: null,
      renotifyingUuid: null
    }
  },
  created () {
    this.loadQuestions()
  },
  watch: {
    domain (newDomain, oldDomain) {
      if (newDomain !== oldDomain) {
        this.loadQuestions()
      }
    }
  },
  methods: {
    stripHtml (html) {
      if (!html) {
        return ''
      }
      if (typeof document === 'undefined') {
        return String(html)
      }
      const container = document.createElement('div')
      container.innerHTML = String(html)
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<\/div>/gi, '\n')
      const text = container.textContent || container.innerText || ''
      return text.replace(/\u00a0/g, ' ').replace(/\n{3,}/g, '\n\n').trim()
    },
    getExportColumnKeys () {
      if (!this.columnApi) {
        return []
      }
      const displayedColumns = this.columnApi.getAllDisplayedColumns().map(column => column.getColId())
      if (!this.columnApi.getColumn(RESPONSE_EXPORT_COL_ID)) {
        return displayedColumns
      }
      const titleIndex = displayedColumns.indexOf('title')
      if (titleIndex === -1) {
        return [...displayedColumns, RESPONSE_EXPORT_COL_ID]
      }
      return [
        ...displayedColumns.slice(0, titleIndex + 1),
        RESPONSE_EXPORT_COL_ID,
        ...displayedColumns.slice(titleIndex + 1)
      ]
    },
    exportWithResponse (format) {
      if (!this.gridApi || !this.columnApi) {
        return
      }
      const columnKeys = this.getExportColumnKeys()
      if (!columnKeys.length) {
        return
      }
      if (format === 'excel') {
        this.gridApi.exportDataAsExcel({ columnKeys })
      } else {
        this.gridApi.exportDataAsCsv({ columnKeys })
      }
    },
    getContextMenuItems (params) {
      const defaultItems = params.defaultItems ? [...params.defaultItems] : []
      const exportItem = {
        name: 'Exporter',
        subMenu: [
          {
            name: 'Exporter CSV',
            action: () => this.exportWithResponse('csv')
          },
          {
            name: 'Exporter Excel (.xlsx)',
            action: () => this.exportWithResponse('excel')
          }
        ]
      }
      const exportIndex = defaultItems.indexOf('export')
      if (exportIndex === -1) {
        return [...defaultItems, exportItem]
      }
      defaultItems.splice(exportIndex, 1, exportItem)
      return defaultItems
    },
    onGridReady (params) {
      this.gridApi = params.api
      this.columnApi = params.columnApi
    },
    // The whole list is already loaded for the grid, so filtering happens client-side.
    search () {
      if (this.searchInput.length > 3) {
        this.gridData = (this.all || []).filter(question => this.matchesSearch(question))
      } else {
        this.gridData = this.all
      }
    },
    normalizeSearchValue (value) {
      return String(value || '')
        .replace(/<[^>]*>/g, ' ')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
    },
    matchesSearch (question) {
      const keywords = Array.isArray(question.keywords) ? question.keywords.join(' ') : question.keywords
      const haystack = this.normalizeSearchValue([
        question.title,
        question.response,
        keywords,
        question.category && question.category.label,
        question.type && question.type.name
      ].filter(Boolean).join(' '))

      return haystack.includes(this.normalizeSearchValue(this.searchInput))
    },

    async goToQuestion ({ data }) {
      this.$router.push({ path: `/app/question/${data.uuid}` })
    },
    loadQuestions () {
      this.loading = true
      http.get('/faq').then(res => {
        this.loading = false
        this.gridData = res.data
        this.all = res.data
        this.clearQuestionSelection()
      }).catch(err => {
        console.log(err)
        this.loading = false
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Nos serveurs rencontrent des difficultés à récuperer la liste des questions',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true,
          icon: 'server'
        })
      })
    },
    loadQuestionsWithPosition () {
      this.saveScrollPosition()
      this.loading = true
      http.get('/faq').then(res => {
        this.loading = false
        this.gridData = res.data
        this.all = res.data
        this.clearQuestionSelection()

        this.$nextTick(() => {
          this.restoreScrollPosition()
        })
      }).catch(err => {
        console.log(err)
        this.loading = false
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Nos serveurs rencontrent des difficultés à récuperer la liste des questions',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true,
          icon: 'server'
        })
      })
    },
    getOrderedUuids () {
      const uuids = []
      if (this.gridApi && this.gridApi.forEachNodeAfterFilterAndSort) {
        this.gridApi.forEachNodeAfterFilterAndSort(node => {
          if (node && node.data && node.data.uuid) uuids.push(node.data.uuid)
        })
      } else if (Array.isArray(this.gridData)) {
        // Fallback to current gridData order
        this.gridData.forEach(row => row && row.uuid && uuids.push(row.uuid))
      }
      return uuids
    },
    openModal ({ data }) {
      if (!this.hasEditionRights) {
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Vous n\'avez pas les droits pour modifier une question',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true,
          icon: 'lock'
        })
        return
      }
      if (data) {
        this.currentQuestion = Object.assign({
          type: { uuid: null },
          category: { uuid: null },
          is_enabled: true,
          keywords: [],
          files: []
        }, data)
        if (!this.currentQuestion.type) this.currentQuestion.type = { uuid: null }
        if (!this.currentQuestion.category) this.currentQuestion.category = { uuid: null }
        if (!this.currentQuestion.keywords) this.currentQuestion.keywords = []
        if (!this.currentQuestion.files) this.currentQuestion.files = []

        // Compute current ordered list and index for navigation
        this.orderedUuids = this.getOrderedUuids()
        this.currentIndex = Math.max(0, this.orderedUuids.indexOf(this.currentQuestion.uuid))
      } else {
        this.currentQuestion = {
          uuid: null,
          title: '',
          response: '',
          type: { uuid: null },
          category: { uuid: null },
          is_enabled: true,
          keywords: [],
          files: []
        }
        // When creating, navigation is not relevant
        this.orderedUuids = []
        this.currentIndex = 0
      }
      this.$modal.show('question-form', this.currentQuestion)
    },
    async handleNavigate ({ direction }) {
      if (!Array.isArray(this.orderedUuids) || this.orderedUuids.length === 0) return
      const nextIndex = this.currentIndex + (direction || 0)
      if (nextIndex < 0 || nextIndex >= this.orderedUuids.length) return
      const nextUuid = this.orderedUuids[nextIndex]
      // Fetch fresh question to reflect latest attachments/edits
      let fresh
      try {
        const res = await http.get(`/faq/${nextUuid}`)
        fresh = res && res.data
      } catch (e) {
        // fallback to existing dataset if request fails
        fresh = (this.gridData || []).find(q => q && q.uuid === nextUuid) || (this.all || []).find(q => q && q.uuid === nextUuid)
      }
      if (!fresh) return

      this.currentQuestion = Object.assign({
        type: { uuid: null },
        category: { uuid: null },
        is_enabled: true,
        keywords: [],
        files: []
      }, fresh)
      if (!this.currentQuestion.type) this.currentQuestion.type = { uuid: null }
      if (!this.currentQuestion.category) this.currentQuestion.category = { uuid: null }
      if (!this.currentQuestion.keywords) this.currentQuestion.keywords = []
      if (!this.currentQuestion.files) this.currentQuestion.files = []
      this.currentIndex = nextIndex
    },
    toggleQuestionEnabled (rowData) {
      if (!this.hasEditionRights) {
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Vous n\'avez pas les droits pour activer ou désactiver une question',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true,
          icon: 'lock'
        })
        return
      }

      this.saveScrollPosition()
      const newStatus = !rowData.is_enabled

      this.loading = true
      http.post(`/faq/${rowData.uuid}`, {
        is_enabled: newStatus ? 1 : 0
      }).then(() => {
        rowData.is_enabled = newStatus
        this.loading = false

        this.$buefy.notification.open({
          duration: 3000,
          message: `La question a bien été ${newStatus ? 'activée' : 'désactivée'}`,
          position: 'is-bottom-right',
          type: 'is-success',
          hasIcon: true
        })

        this.$nextTick(() => {
          this.restoreScrollPosition()
        })
      }).catch(err => {
        console.log(err)
        this.loading = false
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Nos serveurs rencontrent des difficultés à modifier le statut de la question',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true,
          icon: 'server'
        })
      })
    },
    renotifyQuestion (rowData) {
      if (!this.canRenotify || this.renotifyingUuid || renotificationState(rowData).status === 'blocked') return

      this.$dialog.confirm({
        title: 'Notification',
        text: 'Êtes vous sur de vouloir renotifier cette question ?',
        icon: 'warning',
        cancelButtonText: 'Annuler',
        okButtonText: 'Renotifier'
      }).then((result) => {
        if (!result.isOk || result.hideReason !== 'ok') return

        this.renotifyingUuid = rowData.uuid
        http.post(`/faq/${rowData.uuid}/notify`).then(({ data }) => {
          this.$set(rowData, 'renotifications_count', data.renotifications_count)
          this.$set(rowData, 'last_renotified_at', data.last_renotified_at)
          this.$notify({ group: 'maviepro-success', text: 'Notification envoyée.' })
        }).catch(err => {
          console.log(err)
          const message = err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'Erreur lors de l\'envoi de la notification.'
          this.$notify({ group: 'maviepro-error', text: message })
        }).finally(() => {
          this.renotifyingUuid = null
        })
      })
    },
    clearQuestionSelection () {
      this.selectedQuestions = []
      if (this.gridApi) {
        this.gridApi.deselectAll()
      }
    },
    deleteQuestion (rowData) {
      if (!this.hasEditionRights) {
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Vous n\'avez pas les droits pour supprimer une question',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true,
          icon: 'lock'
        })
        return
      }

      this.$buefy.dialog.confirm({
        title: 'Suppression',
        message: 'Êtes-vous sûr de vouloir supprimer cette question ?',
        cancelText: 'Annuler',
        confirmText: 'Supprimer',
        type: 'is-danger',
        onConfirm: () => {
          this.saveScrollPosition()
          this.loading = true
          http.delete(`/faq/${rowData.uuid}`).then(() => {
            this.loading = false
            this.gridData = (this.gridData || []).filter(item => item.uuid !== rowData.uuid)
            this.all = (this.all || []).filter(item => item.uuid !== rowData.uuid)
            if (this.currentQuestion && this.currentQuestion.uuid === rowData.uuid) {
              this.currentQuestion = null
              this.$modal.hide('question-form')
            }

            this.$nextTick(() => {
              this.restoreScrollPosition()
            })

            this.$buefy.notification.open({
              duration: 3000,
              message: `La question "<strong>${rowData.title}</strong>" a bien été supprimée`,
              position: 'is-bottom-right',
              type: 'is-success',
              hasIcon: false
            })
          }).catch(err => {
            console.log(err)
            this.loading = false
            this.$buefy.notification.open({
              duration: 3000,
              message: 'Nos serveurs rencontrent des difficultés à supprimer cette question',
              position: 'is-bottom-left',
              type: 'is-warning',
              hasIcon: true,
              icon: 'server'
            })
          })
        }
      })
    },
    deleteSelectedQuestions () {
      if (!this.hasEditionRights) {
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Vous n\'avez pas les droits pour supprimer des questions',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true,
          icon: 'lock'
        })
        return
      }

      const questions = [...this.selectedQuestions]
      if (!questions.length) {
        return
      }

      this.$buefy.dialog.confirm({
        title: 'Suppression',
        message: `Êtes-vous sûr de vouloir supprimer ${questions.length} question${questions.length > 1 ? 's' : ''} ?`,
        cancelText: 'Annuler',
        confirmText: 'Supprimer',
        type: 'is-danger',
        onConfirm: () => this.confirmDeleteSelectedQuestions(questions)
      })
    },
    async confirmDeleteSelectedQuestions (questions) {
      this.saveScrollPosition()
      this.loading = true
      const deletedUuids = []

      for (const question of questions) {
        try {
          await http.delete(`/faq/${question.uuid}`)
          deletedUuids.push(question.uuid)
        } catch (err) {
          console.log(err)
          this.$buefy.notification.open({
            duration: 3000,
            message: `Impossible de supprimer la question "${question.title || question.uuid}"`,
            position: 'is-bottom-left',
            type: 'is-warning',
            hasIcon: true,
            icon: 'server'
          })
        }
      }

      if (deletedUuids.length) {
        const deletedSet = new Set(deletedUuids)
        this.gridData = (this.gridData || []).filter(item => !deletedSet.has(item.uuid))
        this.all = (this.all || []).filter(item => !deletedSet.has(item.uuid))
        if (this.currentQuestion && deletedSet.has(this.currentQuestion.uuid)) {
          this.currentQuestion = null
          this.$modal.hide('question-form')
        }
        this.$buefy.notification.open({
          duration: 3000,
          message: `${deletedUuids.length} question${deletedUuids.length > 1 ? 's ont été supprimées' : ' a été supprimée'}`,
          position: 'is-bottom-right',
          type: 'is-success',
          hasIcon: false
        })
      }

      this.loading = false
      this.clearQuestionSelection()
      this.$nextTick(() => {
        this.restoreScrollPosition()
      })
    }
  }
}
</script>
